import { CustomFieldService } from "@smeetz/custom-field-common";
import { CustomFieldProvider } from "./custom-field-data-provider";
import { LogLevel, LogService } from "@/services/logging";

import type { ApiConfig } from "@smeetz/custom-field-common";

const DEFAULT_API_CONFIG: ApiConfig = {
  baseUrl: import.meta.env.VITE_SERVICES_API as string,
  timeout: 30000,
};

let customFieldServiceInstance: CustomFieldService | null = null;

/** Reason of the last failed initialization, if any. */
let initFailureReason: string | null = null;

/**
 * Initializes the singleton instance of the CustomFieldService.
 * This should be called once at application startup after Vuex is initialized.
 * Never throws — it runs synchronously inside appSetup, and a throw would
 * abort the whole bootstrap. Failures are recorded and reported by
 * getCustomFieldService instead.
 */
export function initializeCustomFieldService(): void {
  if (!DEFAULT_API_CONFIG.baseUrl) {
    initFailureReason = "VITE_SERVICES_API is not set";
    LogService.info(
      "CustomFieldService initialization failed",
      { reason: initFailureReason },
      LogLevel.Error
    );
    return;
  }

  try {
    customFieldServiceInstance = new CustomFieldService(
      DEFAULT_API_CONFIG,
      new CustomFieldProvider(DEFAULT_API_CONFIG)
    );
    initFailureReason = null;
  } catch (error) {
    initFailureReason = error instanceof Error ? error.message : String(error);
    // Swallowed on purpose (bootstrap must survive); console.error keeps dev
    // visibility while LogService ships it.
    console.error("CustomFieldService creation failed:", error);
    LogService.info(
      "CustomFieldService initialization failed",
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Returns the singleton instance of the CustomFieldService.
 * Throws — naming the initialization failure when there was one — if the
 * service is not ready.
 */
export function getCustomFieldService(): CustomFieldService {
  if (!customFieldServiceInstance) {
    throw new Error(
      `CustomFieldService has not been initialized. Call initializeCustomFieldService() first.${
        initFailureReason
          ? ` Last initialization error: ${initFailureReason}`
          : ""
      }`
    );
  }
  return customFieldServiceInstance;
}
